/**
 * Path-based addressing of an element inside a `<code-island>` shadow root.
 *
 * Format: "childIndex/childIndex/..." (e.g. "0/0/4/0"). Each segment is the
 * element's index among its parent's children. The format is used as the
 * `nodeId` carried on `selectedCodeComponentInnerNode` in `UiNodeStore` —
 * it's intentionally short-lived (compilation can change the tree shape)
 * and matched as a pair of inverse functions:
 * `generateInnerNodePath` ↔ `resolveInnerNodeByPath`.
 */

import {CODE_ISLAND_CONTENT_ROOT_SELECTOR} from './codeIslandContentRoot';

/**
 * Generates a path-based id for a DOM element relative to a root element.
 *
 * Each segment is the element's index among its parent's children.
 * This is the inverse of `resolveInnerNodeByPath`.
 */
export function generateInnerNodePath(element: Element, root: Element): string {
  const parts: string[] = [];
  let current: Element | null = element;

  while (current && current !== root) {
    const parent: Element | null = current.parentElement;
    if (!parent) break;

    const index = Array.from(parent.children).indexOf(current);
    parts.unshift(String(index));
    current = parent;
  }

  return parts.join('/');
}

/**
 * The node that inner-node paths are relative to: `[data-root]` when it holds the
 * rendered content, else the shadow root itself (some renderers place content as
 * siblings rather than descendants).
 *
 * Exported so callers that GENERATE paths pick the same origin this file walks
 * from — `generateInnerNodePath` and `resolveInnerNodeByPath` are only inverses
 * while they agree on the root.
 */
export function resolveInnerNodeRoot(shadowRoot: ShadowRoot): ParentNode {
  const dataRoot = shadowRoot.querySelector(CODE_ISLAND_CONTENT_ROOT_SELECTOR);
  return dataRoot && dataRoot.childElementCount > 0 ? dataRoot : shadowRoot;
}

/**
 * Resolves a DOM element inside a shadow root using a path-based node id.
 *
 * Given a shadow root and a `nodeId` previously produced by
 * `generateInnerNodePath`, walks the DOM by child index at each level to
 * find the matching element.
 *
 * When `[data-root]` has no children (some renderers place content as
 * siblings rather than descendants), falls back to walking from the
 * shadow root itself.
 */
export function resolveInnerNodeByPath(
  shadowRoot: ShadowRoot,
  nodeId: string
): HTMLElement | null {
  const root = resolveInnerNodeRoot(shadowRoot);

  const parts = nodeId.split('/');
  let current: ParentNode = root;

  for (const part of parts) {
    const index = parseInt(part, 10);
    if (isNaN(index)) return null;

    const child = current.children[index];
    if (!child) return null;

    current = child;
  }

  return current as HTMLElement;
}

export type CodeComponentInnerNodeSelection = {
  nodeId: string;
};
